import * as React from 'react';
import { cn } from '../lib/utils';

export interface DescriptionItem {
  key?: string;
  label: React.ReactNode;
  children: React.ReactNode;
  /** Spans the full row regardless of `columns`. */
  full?: boolean;
}

export interface DescriptionListProps {
  items: DescriptionItem[];
  /** Columns at `sm` and up. Always one column on phones. Ignored by `rows`. */
  columns?: 1 | 2 | 3 | 4;
  /**
   * `grid` — muted label stacked over its value, in columns.
   * `rows` — one pair per line, label leading and value trailing, separated by
   * hairlines; for a narrow card of facts.
   */
  layout?: 'grid' | 'rows';
  className?: string;
}

const GRID: Record<NonNullable<DescriptionListProps['columns']>, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
};

/**
 * Label/value pairs — the replacement for antd's `<Descriptions>`. Labels are
 * muted and small, values carry the weight.
 */
export function DescriptionList({
  items,
  columns = 2,
  layout = 'grid',
  className,
}: DescriptionListProps) {
  if (layout === 'rows') {
    return (
      <dl className={cn('divide-y divide-border/60', className)}>
        {items.map((item, index) => (
          <div
            key={item.key ?? index}
            className="flex items-baseline justify-between gap-4 py-2 first:pt-0 last:pb-0"
          >
            <dt className="shrink-0 text-[13px] text-muted-foreground">{item.label}</dt>
            <dd className="min-w-0 text-end text-sm wrap-break-word">{item.children}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-4', GRID[columns], className)}>
      {items.map((item, index) => (
        <div
          key={item.key ?? index}
          className={cn('grid min-w-0 gap-1', item.full && 'col-span-full')}
        >
          <dt className="text-xs text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 text-sm wrap-break-word">{item.children}</dd>
        </div>
      ))}
    </dl>
  );
}
